import { Component, NgZone, OnInit } from '@angular/core';
import { SafeArea } from 'capacitor-plugin-safe-area';
import { Router, RouterOutlet } from '@angular/router';
import { distinctUntilChanged } from 'rxjs';
import { ThemeService } from './theme.service';
import { LanguageService } from './language.service';
import { pendingDeepLink$ } from './deep-link';
import { resolveDeepLinkTarget } from './deep-link-target';
import { environment } from '../environments/environment';
import { BackButtonService } from './back-button.service';

@Component({
  selector: 'nbd-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  imports: [RouterOutlet],
})
export class AppComponent implements OnInit {
  // Both services are providedIn:'root' (lazy) but must be alive from boot:
  //  - ThemeService applies the persisted light/dark/system preference.
  //  - LanguageService's constructor loads the active translation bundle
  //    (ts.use()). It used to be instantiated only as a side effect of
  //    <nbd-language-selector>; once that was removed from the scanner, a
  //    deep-link / reload straight onto the scanner left i18n unloaded (raw
  //    keys). Injecting it here loads translations regardless of the first route.
  constructor(
    private theme: ThemeService,
    private language: LanguageService,
    private router: Router,
    private zone: NgZone,
    // Must exist from boot: it owns the app's single hardware-back listener, and
    // until it is constructed Capacitor's raw goBack() default is what runs.
    private backButton: BackButtonService
  ) {
    // Deep links (Universal/App Links) are captured in main.ts before bootstrap
    // and replayed here; route them into the same funnel a scanned QR uses.
    pendingDeepLink$
      .pipe(distinctUntilChanged())
      .subscribe((url) => this.handleDeepLink(url));

    SafeArea.addListener('safeAreaChanged', (data) => {
      console.log(data);
      const { insets } = data;
      for (const [key, value] of Object.entries(insets)) {
        document.documentElement.style.setProperty(
          `--safe-area-inset-${key}`,
          `${value}px`
        );
      }
    });
  }

  async ngOnInit() {
    SafeArea.getSafeAreaInsets().then(({ insets }) => {
      console.log(insets);
      for (const [key, value] of Object.entries(insets)) {
        document.documentElement.style.setProperty(
          `--safe-area-inset-${key}`,
          `${value}px`
        );
      }
    });
  }

  /**
   * Route a captured deep link into the app (see resolveDeepLinkTarget for the
   * parse + host-guard). Capacitor fires the listener outside Angular's zone, so
   * navigate inside zone.run() to keep change detection alive.
   */
  private handleDeepLink(raw: string): void {
    const target = resolveDeepLinkTarget(raw, environment.clientUrl);
    if (!target) {
      return;
    }
    this.zone.run(() =>
      this.router.navigate(['/checkin', target.eventId], {
        queryParams: { pin: target.pin },
      })
    );
  }
}
